import {
  reduce,
  keys,
  forEach,
  assign
} from 'min-dash';

import {
  getBusinessObject
} from '../../../base/BaseServices';

const DEFAULT_FLOW = 'default';
const ID = 'id';
const DI = 'di';

const NULL_DIMENSIONS = {
  width: 0,
  height: 0
};

export default function UpdatePropertiesHandler(
  elementRegistry, moddle, translate,
  modeling, textRenderer
) {
  this._elementRegistry = elementRegistry;
  this._moddle = moddle;
  this._translate = translate;
  this._modeling = modeling;
  this._textRenderer = textRenderer;
}

UpdatePropertiesHandler.$inject = [
  'elementRegistry',
  'moddle',
  'translate',
  'modeling',
  'textRenderer'
];

UpdatePropertiesHandler.prototype.execute = function (context) {
  const element = context.element;
  const changed = [element];
  const translate = this._translate;

  if (!element) {
    throw new Error(translate('element required'));
  }

  const elementRegistry = this._elementRegistry;
  const ids = this._moddle.ids;
  
  const businessObject = element.businessObject;
  const properties = unwrapBusinessObjects(context.properties);
  const oldProperties = context.oldProperties || getProperties(businessObject, properties);

  if (isIdChange(properties, businessObject)) {
    ids.unclaim(businessObject[ID]);
    elementRegistry.updateId(element, properties[ID]);
    ids.claim(properties[ID], businessObject);
  }

  if (DEFAULT_FLOW in properties) {
    if (properties[DEFAULT_FLOW]) {
      changed.push(elementRegistry.get(properties[DEFAULT_FLOW].id));
    }

    if (businessObject[DEFAULT_FLOW]) {
      changed.push(elementRegistry.get(businessObject[DEFAULT_FLOW].id));
    }
  }

  setProperties(businessObject, properties);

  context.oldProperties = oldProperties;
  context.changed = changed;

  return changed;
};

UpdatePropertiesHandler.prototype.postExecute = function (context) {
  const element = context.element;
  const label = element.label;

  const text = label && getBusinessObject(label).name;

  if (!text) {
    return;
  }

  const newLabelBounds = this._textRenderer.getExternalLabelBounds(label, text);

  this._modeling.resizeShape(label, newLabelBounds, NULL_DIMENSIONS);
};

UpdatePropertiesHandler.prototype.revert = function (context) {
  const element = context.element;
  const properties = context.properties;
  const oldProperties = context.oldProperties;
  const businessObject = element.businessObject;
  const elementRegistry = this._elementRegistry;
  const ids = this._moddle.ids;

  setProperties(businessObject, oldProperties);

  if (isIdChange(properties, businessObject)) {
    ids.unclaim(properties[ID]);
    elementRegistry.updateId(element, oldProperties[ID]);
    ids.claim(oldProperties[ID], businessObject);
  }

  return context.changed;
};

function isIdChange(properties, businessObject) {
  return ID in properties && properties[ID] !== businessObject[ID];
}

function getProperties(businessObject, properties) {
  const propertyNames = keys(properties);

  return reduce(propertyNames, function (result, key) {
    if (key !== DI) {
      result[key] = businessObject.get(key);
    } else {
      result[key] = getDiProperties(businessObject.di, keys(properties.di));
    }

    return result;
  }, {});
}

function getDiProperties(di, propertyNames) {
  return reduce(propertyNames, function (result, key) {
    result[key] = di.get(key);

    return result;
  }, {});
}

function setProperties(businessObject, properties) {
  forEach(properties, function (value, key) {
    if (key !== DI) {
      businessObject.set(key, value);
    } else {
      if (businessObject.di) {
        setDiProperties(businessObject.di, value);
      }
    }
  });
}

function setDiProperties(di, properties) {
  forEach(properties, function (value, key) {
    di.set(key, value);
  });
}

const referencePropertyNames = ['default'];

function unwrapBusinessObjects(properties) {
  const unwrappedProps = assign({}, properties);

  referencePropertyNames.forEach(function (name) {
    if (name in properties) {
      unwrappedProps[name] = getBusinessObject(unwrappedProps[name]);
    }
  });

  return unwrappedProps;
}
